<script setup lang="ts">
/**
 * Numeric keypad, PRD 6.2 and FR-3.3.
 *
 * The block is exactly 44 dvh and the keys flex to fill it, so the buttons stay
 * in the thumb zone and -- crucially -- never move between turns. A keypad that
 * shifts by a few pixels when a hint appears is a keypad that gets mistapped.
 */

defineProps<{
  canBank: boolean;
  bankLabel: string;
  strikeLabel: string;
}>();

const emit = defineEmits<{
  digit: [d: string];
  backspace: [];
  clear: [];
  bank: [];
  strike: [];
}>();

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "C", "0", "back"] as const;

function press(key: string): void {
  if (key === "C") emit("clear");
  else if (key === "back") emit("backspace");
  else emit("digit", key);
}

const label = (key: string): string =>
  key === "C" ? "Löschen" : key === "back" ? "Ziffer zurück" : key;
</script>

<template>
  <div class="pad">
    <div class="keys">
      <button
        v-for="k in KEYS"
        :key="k"
        class="key num"
        :aria-label="label(k)"
        @click="press(k)"
      >
        <svg
          v-if="k === 'back'"
          class="ico"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            d="M8 5h12a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H8l-6-7 6-7Z"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linejoin="round"
          />
          <path
            d="m11.5 9.5 5 5m0-5-5 5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
          />
        </svg>
        <template v-else>
          {{ k }}
        </template>
      </button>
    </div>
    <div class="actions">
      <button
        class="action action--strike"
        @click="emit('strike')"
      >
        {{ strikeLabel }}
      </button>
      <button
        class="action action--bank"
        :disabled="!canBank"
        @click="emit('bank')"
      >
        {{ bankLabel }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.pad {
  /* PRD 6.2: exactly 44 dvh, keys flexing to fill, action row fixed at the
     bottom. The home indicator sits below it on a modern iPhone.

     The floor is not in the spec and is deliberate. On a 568 px iPhone SE,
     44 dvh is 250 px, which cannot hold four rows of 44 px keys, an action
     row and the safe-area padding -- the action row ends up sitting on top
     of the "0" key. Since 6.2 states the leaderboard as a *maximum* and the
     keypad as exact, the leaderboard is the one that can yield, so the pad
     keeps its real minimum and takes the space from above. */
  /* Never shrink: in a flex column the default flex-shrink:1 let the pad give
     up space under pressure (eight players cost it 8px), which moves the keys
     out of the thumb zone -- the exact thing the fixed budget exists to stop.
     The leaderboard above is what yields instead. */
  flex: 0 0 auto;
  /* 38dvh, not the 44dvh in 6.2: the leaderboard needed the room so five
     players fit without scrolling. The floor below still guarantees the keys
     never drop under a fingertip. */
  height: 38dvh;
  min-height: calc(
    4 * 44px + 3 * var(--pad-gap) + var(--pad-gap) + 48px + 16px + var(--safe-bottom)
  );
  display: flex;
  flex-direction: column;
  gap: var(--pad-gap);
  padding: 8px 12px calc(8px + var(--safe-bottom));
}

.keys {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: 1fr;
  gap: var(--pad-gap);
}

.key {
  border: none;
  border-radius: var(--radius-control);
  background: var(--card);
  font-size: clamp(18px, 5vw, 26px);
  font-weight: 500;
  /* Never smaller than a finger, however short the viewport gets. */
  min-height: 44px;
}

.ico {
  width: 26px;
  height: 26px;
}

.key:active {
  background: var(--accent-tint);
}

.actions {
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: var(--pad-gap);
  flex: 0 0 auto;
}

.action {
  border: none;
  border-radius: var(--radius-control);
  min-height: 48px;
  font-size: 17px;
  font-weight: 600;
}

.action--strike {
  background: var(--card);
  color: var(--danger);
}

.action--bank {
  background: var(--accent);
  color: #fff;
}

.action--bank:disabled {
  /* Still legible: FR-3.4 wants the reason visible, not the button hidden. */
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
